import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Badge } from "../ui/Badge";
import { ArrowUp, ArrowDown, SquarePen, Save, X } from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import heartLoading from "../../../assests/heartLoading.gif";
import AIEditPage from "./AIEditPage";
import { useFeatureFlag } from "../../../context/FeatureFlagContext";
import TableMultiSelect from "./TableMultiSelect";


const PAGE_SIZE = 80;

const AssetDataTable = ({
  tableOverview,
  getTypeColor,
  id,
  setEditMetadata,
  loading,
}) => {
  // ── Sorting ───────────────────────────────────────────────────────────────
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder,  setSortOrder]  = useState("asc");

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const [isEditMode,   setIsEditMode]   = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [activePopup,  setActivePopup]  = useState(null);
  const [editedRows,   setEditedRows]   = useState(new Set());

  // ── AI Edit page ──────────────────────────────────────────────────────────
  const [aiEditRow, setAiEditRow] = useState(null);
  const isAIEditEnabled = useFeatureFlag("aiMetadataEdit");

  // ── Glossary options ──────────────────────────────────────────────────────
  const [glossaryOptions, setGlossaryOptions] = useState([]);
  const [glossaryLoading, setGlossaryLoading] = useState(false);

  // ── Column search ─────────────────────────────────────────────────────────
  const [columnSearch, setColumnSearch] = useState({
    name: "", type: "", is_cde: "", metadata: "",
    businessterm: "", transformationLogic: "", sample: "",
  });

  // ── Virtual scroll ────────────────────────────────────────────────────────
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollContainerRef = useRef(null);

  // ── Fetch glossary options when edit mode is activated ────────────────────
  const fetchGlossaryOptions = async () => {
    setGlossaryLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/get-asset-glossary`);
      setGlossaryOptions(res.data || []);
    } catch (err) {
      console.error("Error fetching glossary options:", err);
      setGlossaryOptions([]);
    } finally {
      setGlossaryLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode || glossaryOptions.length > 0) return;
    fetchGlossaryOptions();
  }, [isEditMode]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSort = (column) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleColumnSearchChange = (column, value) => {
    setColumnSearch((prev) => ({ ...prev, [column]: value }));
    setVisibleCount(PAGE_SIZE); // reset to first batch on new search
  };

  const handleFieldChange = (index, field, value) => {
    setEditableData((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
    const rowId = editableData[index]?.attribute_id;
    if (rowId) setEditedRows((prev) => new Set(prev).add(rowId));
  };

  const handleSave = async () => {
    const modifiedRows = editableData.filter((row) => editedRows.has(row.attribute_id));
    if (modifiedRows.length === 0) { setIsEditMode(false); return; }

    const columnsData = modifiedRows.map((row) => ({
      column_name:           row?.name              || "N/A",
      is_cde:                row?.is_cde            || false,
      technical_description: row?.metadata,
      business_description:  row?.businessterm,
      transformation_logic:  row?.transformationLogic,
      glossary_term:         row?.glossary_term     || [],
    }));

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/assets/${id}/metadata`,
        { columns: columnsData }
      );
      setEditedRows(new Set());
      setIsEditMode(false);
      toast.success("Metadata saved successfully.");
      setEditMetadata((prev) => !prev);
    } catch (err) {
      console.error(err);
      toast.error("Meta save failed.");
    }
  };

  const handleOpenAIEdit = (attr) => {
    setAiEditRow({
      id:                  attr.attribute_id,
      fieldName:           attr.name                 || "",
      type:                attr.type                 || "string",
      cdk:                 attr.is_cde ? "Yes" : "No",
      technicalMetadata:   attr.metadata             || "N/A",
      businessDescription: attr.businessterm         || "N/A",
      transformationLogic: attr.transformationLogic  || "N/A",
      sampleValue:         attr.sample               || "N/A",
    });
  };

  const handleAISaveRow = (attributeId, updates) => {
    setEditableData((prev) =>
      prev.map((row) => {
        if (row.attribute_id !== attributeId) return row;
        const next = { ...row };
        if (updates.technicalMetadata   !== undefined) next.metadata           = updates.technicalMetadata;
        if (updates.businessDescription !== undefined) next.businessterm        = updates.businessDescription;
        if (updates.transformationLogic !== undefined) next.transformationLogic = updates.transformationLogic;
        return next;
      })
    );
    setEditedRows((prev) => new Set(prev).add(attributeId));
  };

  // ── Filtered + sorted data (all rows, in memory) ──────────────────────────
  const filteredAndSortedData = useMemo(() => {
    if (!tableOverview || !Array.isArray(tableOverview)) return [];
    let filtered = [...tableOverview];

    Object.entries(columnSearch).forEach(([column, searchValue]) => {
      if (searchValue?.trim()) {
        filtered = filtered.filter((row) => {
          const val = column === "is_cde" ? (row[column] ? "yes" : "no") : (row[column] || "");
          return String(val).toLowerCase().includes(searchValue.toLowerCase().trim());
        });
      }
    });

    if (sortColumn) {
      filtered.sort((a, b) => {
        const aVal = sortColumn === "is_cde" ? (a[sortColumn] ? "yes" : "no") : (a[sortColumn] ?? "");
        const bVal = sortColumn === "is_cde" ? (b[sortColumn] ? "yes" : "no") : (b[sortColumn] ?? "");
        const cmp  = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
        return sortOrder === "asc" ? cmp : -cmp;
      });
    }
    return filtered;
  }, [tableOverview, columnSearch, sortColumn, sortOrder]);

  // Reset visible count when filters/sort produce a new result set
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filteredAndSortedData.length, sortColumn, sortOrder]);

  // Rows currently visible in the table
  const currentData = filteredAndSortedData.slice(0, visibleCount);
  const hasMore     = visibleCount < filteredAndSortedData.length;

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight * 0.5 && visibleCount < filteredAndSortedData.length) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredAndSortedData.length));
    }
  }, [visibleCount, filteredAndSortedData.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // ── Sync editableData when tableOverview changes ──────────────────────────
  useEffect(() => {
    if (tableOverview) {
      setEditableData(tableOverview.map((row) => ({
        ...row,
        glossary: row.glossary || "",
        glossary_term:
          Array.isArray(row.glossary_term) && row.glossary_term.length > 0
            ? row.glossary_term
            : row.glossary && row.glossary !== "N/A"
              ? String(row.glossary).split(",").map((s) => s.trim()).filter(Boolean)
              : [],
      })));
    }
  }, [tableOverview]);

  // ── Column config ─────────────────────────────────────────────────────────
  const columns = [
    { key: "name",                label: "Field Name"           },
    { key: "type",                label: "Type"                 },
    { key: "is_cde",              label: "CDE"                  },
    { key: "metadata",            label: "Technical Metadata"   },
    { key: "businessterm",        label: "Business Description" },
    { key: "transformationLogic", label: "Transformation Logic" },
    { key: "glossary",            label: "Glossary"             },
    { key: "sample",              label: "Sample Value"         },
    { key: "actions",             label: "Actions"              },
  ];

  // ── AI Edit full-page swap ────────────────────────────────────────────────
  if (aiEditRow) {
    return (
      <AIEditPage
        row={aiEditRow}
        assetId={id}
        onBack={() => setAiEditRow(null)}
        onSaveRow={handleAISaveRow}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-700 text-[20px] text-center">
            Loading assets data...
          </span>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div>
      <Toaster />

      {/* ── Admin edit / save buttons ── */}
      {localStorage.getItem("role") === "Admin" && (
        <>
          <div className="flex justify-end gap-2 mb-1 px-1">
            {isEditMode ? (
              <button
                className="h-[36px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg px-4"
                onClick={() => {
                  setEditableData(tableOverview.map((row) => ({
                    ...row,
                    glossary: row.glossary || "",
                    glossary_term:
                      Array.isArray(row.glossary_term) && row.glossary_term.length > 0
                        ? row.glossary_term
                        : row.glossary && row.glossary !== "N/A"
                          ? String(row.glossary).split(",").map((s) => s.trim()).filter(Boolean)
                          : [],
                  })));
                  setEditedRows(new Set());
                  setIsEditMode(false);
                }}
              >
                <X className="h-5" /> Cancel
              </button>
            ) : (
              <button
                className="h-[36px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg px-4"
                onClick={() => setIsEditMode(true)}
              >
                <SquarePen className="h-5" /> Edit
              </button>
            )}
            <button
              disabled={!isEditMode && editedRows.size === 0}
              className="h-[40px] text-[#bb1ccc] disabled:text-gray-200 hover:text-white transition-colors duration-200 border border-[#bb1ccc] disabled:border-gray-200 disabled:cursor-not-allowed p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] disabled:bg-gray-500 hover:shadow-lg px-4"
              onClick={handleSave}
            >
              <Save className="h-5" /> Save
            </button>
          </div>

          {(isEditMode || editedRows.size > 0) && (
            <div className="flex items-center gap-2 mx-4 mb-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" className="shrink-0">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span className="text-[12px] text-amber-700 font-medium">
                Please save your changes before leaving this page, otherwise you will lose your changes.
              </span>
            </div>
          )}
        </>
      )}

      {/* ── Count bar ── */}
      <div className="flex items-center justify-between px-4 pb-2 text-[11px] text-gray-500">
        <span>
          Showing <span className="font-medium text-gray-700">{currentData.length}</span> of{" "}
          <span className="font-medium text-gray-700">{filteredAndSortedData.length}</span> columns
          {tableOverview && filteredAndSortedData.length !== tableOverview.length && (
            <span className="text-gray-400 ml-1">(filtered from {tableOverview.length} total)</span>
          )}
        </span>
      </div>

      {/* ── Table — scrollable container with sticky header ── */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
       
        <div ref={scrollContainerRef} className="overflow-x-auto overflow-y-auto max-h-[600px]">
          <table className="w-full">

            {/* ── Sticky header ── */}
            <thead
              className="bg-[#F0FDFA] text-[#404040] font-normal"
              style={{ position: "sticky", top: 0, zIndex: 10 }}
            >
              {/* Sort row */}
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.key !== "actions" && handleSort(col.key)}
                    className={`px-3 pb-[6px] pr-[2px] text-[12px] pt-[10px] text-left whitespace-nowrap w-[14%] select-none font-normal ${col.key !== "actions" ? "cursor-pointer" : ""}`}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {col.key !== "actions" && (
                        sortColumn === col.key
                          ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                          : <ArrowUp size={12} className="opacity-30" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>

              {/* Column search row */}
              <tr>
                {columns.map((col) => (
                  <th key={`search-${col.key}`} className="px-3 pt-0 pb-[10px] w-[14px] font-normal">
                    {col.key !== "actions" ? (
                      <input
                        className="w-full text-[11px] pb-[5px] pl-[10px] placeholder-gray-400 bg-white rounded py-2 outline-none font-normal placeholder:font-normal"
                        placeholder={col.key === "is_cde" ? "Yes/No" : "Search"}
                        value={columnSearch[col.key] || ""}
                        onChange={(e) => handleColumnSearchChange(col.key, e.target.value)}
                      />
                    ) : (
                      <div className="h-[34px]" />
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            {/* ── Body ── */}
            <tbody className="divide-y divide-gray-200 bg-white">
              {!currentData || currentData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-gray-500">
                    No data found matching your search criteria
                  </td>
                </tr>
              ) : (
                currentData.map((attr, index) => (
                  <tr key={attr.attribute_id || index} className="hover:bg-gray-50 transition-colors">

                    {/* Field Name */}
                    <td className="py-2 px-3 text-[11px]">
                      <div className="font-normal text-gray-900">{attr.name}</div>
                    </td>

                    {/* Type */}
                    <td className="py-2 px-3 text-[11px]">
                      <Badge variant={getTypeColor(attr.type)} size="sm">{attr.type}</Badge>
                    </td>

                    {/* CDE */}
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="relative w-24">
                          <select
                            value={editableData[index]?.is_cde ? "Yes" : "No"}
                            onChange={(e) => handleFieldChange(index, "is_cde", e.target.value === "Yes")}
                            className="w-full h-[35px] text-[11px] px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] hover:border-gray-400 transition-all duration-150 appearance-none cursor-pointer"
                          >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
                            <svg className="h-[15px] w-[15px]" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${attr.is_cde ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                          {attr.is_cde ? "Yes" : "No"}
                        </span>
                      )}
                    </td>

                    {/* Technical Metadata */}
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editableData[index]?.metadata || ""}
                            onChange={(e) => handleFieldChange(index, "metadata", e.target.value)}
                            placeholder="Enter metadata"
                            className="w-full h-[35px] text-[11px] px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] hover:border-gray-400 transition-all duration-150"
                          />
                          <button type="button" onClick={() => setActivePopup({ index, field: "metadata" })} className="px-2 text-gray-500 hover:text-gray-700" title="Expand">⤢</button>
                        </div>
                      ) : (
                        <span className="text-gray-600 text-[11px]">{editableData[index]?.metadata || attr.metadata || "—"}</span>
                      )}
                    </td>

                    {/* Business Description */}
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="flex gap-1">
                          <input
                            value={editableData[index]?.businessterm || ""}
                            onChange={(e) => handleFieldChange(index, "businessterm", e.target.value)}
                            className="w-full h-[35px] text-[11px] px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] hover:border-gray-400 transition-all duration-150"
                          />
                          <button onClick={() => setActivePopup({ index, field: "businessterm" })}>⤢</button>
                        </div>
                      ) : (
                        <span className="text-gray-600 text-[11px]">{editableData[index]?.businessterm || attr.businessterm || "—"}</span>
                      )}
                    </td>

                    {/* Transformation Logic */}
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="flex gap-1">
                          <input
                            value={editableData[index]?.transformationLogic || ""}
                            onChange={(e) => handleFieldChange(index, "transformationLogic", e.target.value)}
                            className="w-full h-[35px] text-[11px] px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] hover:border-gray-400 transition-all duration-150"
                          />
                          <button onClick={() => setActivePopup({ index, field: "transformationLogic" })}>⤢</button>
                        </div>
                      ) : (
                        <span className="text-gray-600 text-[11px]">{editableData[index]?.transformationLogic || attr.transformationLogic || "N/A"}</span>
                      )}
                    </td>

                    {/* Glossary */}
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <TableMultiSelect
                          options={glossaryOptions.map((g) => ({ label: g.display_name, value: g.name }))}
                          value={editableData[index]?.glossary_term || []}
                          onChange={(values) => handleFieldChange(index, "glossary_term", values)}
                          placeholder="Select"
                          loading={glossaryLoading}
                          noOptionsText="No glossary terms found"
                        />
                      ) : (
                        <span className="text-gray-600 text-[11px]">
                          {Array.isArray(attr.glossary_term) && attr.glossary_term.length > 0
                            ? attr.glossary_term.join(", ")
                            : attr.glossary || "N/A"}
                        </span>
                      )}
                    </td>

                    {/* Sample Value */}
                    <td className="py-2 px-3 text-[11px] text-gray-600">
                      {attr.sample || "N/A"}
                    </td>

                    {/* Actions */}
                   {isAIEditEnabled && (
                    <td className="py-2 px-3 text-[11px]">
                      <button
                        onClick={() => handleOpenAIEdit(attr)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#99f6e4] text-[#0d9488] bg-white text-[11px] font-medium hover:bg-[#f0fdfa] transition-colors whitespace-nowrap"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ display: "inline", verticalAlign: "middle" }}>
                          <path d="M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z" />
                        </svg>
                        AI Edit
                      </button>
                    </td>
                    )}

                  </tr>
                ))
              )}

              {/* ── Scroll sentinel ── */}
              <tr>
                <td colSpan={columns.length} className="p-0">
                  {hasMore && (
                    <div className="flex items-center justify-center gap-2 py-3">
                      <img src={heartLoading} alt="Loading more..." className="h-6 w-6" />
                      <span className="text-gray-400 text-[11px]">Loading more rows...</span>
                    </div>
                  )}
                  {!hasMore && filteredAndSortedData.length > PAGE_SIZE && (
                    <div className="text-center py-3 text-gray-400 text-[11px]">
                      All {filteredAndSortedData.length} rows loaded
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {/* ── Expand popup ── */}
          {activePopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <div className="bg-white w-[640px] max-w-[92%] rounded-lg shadow-xl border border-gray-200">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700">Edit {activePopup.field}</h3>
                  <button onClick={() => setActivePopup(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5" /></button>
                </div>
                <div className="p-4">
                  <textarea
                    value={editableData[activePopup.index][activePopup.field] || ""}
                    onChange={(e) => handleFieldChange(activePopup.index, activePopup.field, e.target.value)}
                    className="w-full h-64 resize-none text-sm text-gray-700 border border-gray-300 rounded-md p-3 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc]"
                    placeholder="Enter detailed information..."
                  />
                </div>
                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                  <button onClick={() => setActivePopup(null)} className="px-4 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100">Cancel</button>
                  <button onClick={() => setActivePopup(null)} className="px-4 py-1.5 text-sm bg-[#bb1ccc] text-white rounded-md hover:bg-[#00008a] shadow-sm">Done</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetDataTable;
